import { useState } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from 'recharts';
import { ITestDefinition } from '../../models/testDefinition';
import { Athlete, TimeframeKey, timeframeLabels } from './testingTypes';
import { ITestResult } from '../../models/testResult';
import { useCrossTestScatter } from '../hooks/useTeamAnalytics';
import styles from './CrossTestScatter.module.scss';

interface CrossTestScatterProps {
  categoryTests: ITestDefinition[];
  athletes: Athlete[];
  historyMap: Record<string, ITestResult[]>;
  globalTimeframe: TimeframeKey;
  onTimeframeChange: (tf: TimeframeKey) => void;
}

/** Tooltip personalizzato che mostra nome atleta + valori X/Y (sez.8.3) */
function ScatterTooltipContent({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className={styles.tooltipBox}>
      <span className={styles.tooltipName}>{d.athleteName}</span>
      <span className={styles.tooltipValues}>
        X: {d.x} · Y: {d.y}
      </span>
    </div>
  );
}

/**
 * Vista relazioni cross-test (spec sez.8.3).
 * Due selettori (asse X / asse Y) tra i test della categoria corrente.
 * Plotta un punto per ogni atleta che ha entrambi i valori.
 * Mostra il coefficiente di correlazione di Pearson r se calcolabile.
 */
export default function CrossTestScatter({
  categoryTests,
  athletes,
  historyMap,
  globalTimeframe,
  onTimeframeChange,
}: CrossTestScatterProps) {
  const [testIdX, setTestIdX] = useState<string>(categoryTests[0]?.id ?? '');
  const [testIdY, setTestIdY] = useState<string>(categoryTests[1]?.id ?? categoryTests[0]?.id ?? '');

  const { points, pearsonR } = useCrossTestScatter(
    athletes,
    historyMap,
    globalTimeframe,
    testIdX || null,
    testIdY || null,
  );

  const testX = categoryTests.find((t) => t.id === testIdX);
  const testY = categoryTests.find((t) => t.id === testIdY);

  /** Etichetta correlazione con colore direzionale */
  const correlationLabel = (): { text: string; className: string } | null => {
    if (pearsonR === null) return null;
    const abs = Math.abs(pearsonR);
    let strength = '';
    if (abs >= 0.7) strength = 'forte';
    else if (abs >= 0.4) strength = 'moderata';
    else strength = 'debole';

    const direction = pearsonR >= 0 ? 'positiva' : 'negativa';
    return {
      text: `r = ${pearsonR.toFixed(2)} — correlazione ${strength} ${direction}`,
      className: abs >= 0.7 ? styles.corrStrong : abs >= 0.4 ? styles.corrModerate : styles.corrWeak,
    };
  };

  const corr = correlationLabel();

  const sameTest = testIdX && testIdY && testIdX === testIdY;

  return (
    <div className={styles.scatterRoot}>
      {/* Selettori assi + timeframe */}
      <div className={styles.controls}>
        <div className={styles.axisSelectors}>
          <div className={styles.selectorGroup}>
            <label className={styles.selectorLabel}>Asse X</label>
            <select
              className={styles.select}
              value={testIdX}
              onChange={(e) => setTestIdX(e.target.value)}
            >
              <option value="">— Seleziona —</option>
              {categoryTests.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.selectorGroup}>
            <label className={styles.selectorLabel}>Asse Y</label>
            <select
              className={styles.select}
              value={testIdY}
              onChange={(e) => setTestIdY(e.target.value)}
            >
              <option value="">— Seleziona —</option>
              {categoryTests.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.timeframePills}>
          {(Object.keys(timeframeLabels) as TimeframeKey[]).map((tf) => (
            <button
              key={tf}
              onClick={() => onTimeframeChange(tf)}
              className={[
                styles.timeframePill,
                globalTimeframe === tf ? styles.timeframePillActive : '',
              ].filter(Boolean).join(' ')}
            >
              {timeframeLabels[tf]}
            </button>
          ))}
        </div>
      </div>

      {/* Messaggio di guardia: stessi test selezionati */}
      {sameTest && (
        <p className={styles.emptyNote}>Seleziona due test differenti per vedere la relazione.</p>
      )}

      {/* Messaggio: nessun dato disponibile */}
      {!sameTest && testIdX && testIdY && points.length === 0 && (
        <p className={styles.emptyNote}>
          Nessun atleta ha dati per entrambi i test nel timeframe selezionato.
        </p>
      )}

      {/* Badge correlazione Pearson */}
      {corr && points.length > 0 && (
        <div className={[styles.corrBadge, corr.className].join(' ')}>
          {corr.text}
          <span className={styles.corrN}>&nbsp;(n = {points.length})</span>
        </div>
      )}

      {/* Grafico scatter */}
      {!sameTest && testIdX && testIdY && points.length > 0 && (
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 30, left: 10, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" />
              <XAxis
                type="number"
                dataKey="x"
                domain={['auto', 'auto']}
                tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi:Medium',sans-serif" }}
                tickLine={false}
              >
                <Label
                  value={testX?.name ?? ''}
                  offset={-10}
                  position="insideBottom"
                  style={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi:Medium',sans-serif" }}
                />
              </XAxis>
              <YAxis
                type="number"
                dataKey="y"
                domain={['auto', 'auto']}
                tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi:Medium',sans-serif" }}
                tickLine={false}
                axisLine={false}
              >
                <Label
                  value={testY?.name ?? ''}
                  angle={-90}
                  position="insideLeft"
                  offset={20}
                  style={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi:Medium',sans-serif" }}
                />
              </YAxis>
              <Tooltip content={<ScatterTooltipContent />} />
              {/* Linea di tendenza visiva opzionale: media X e Y come riferimento */}
              {points.length >= 2 && (
                <>
                  <ReferenceLine
                    x={points.reduce((a, p) => a + p.x, 0) / points.length}
                    stroke="#b3b3b3"
                    strokeDasharray="4 4"
                  />
                  <ReferenceLine
                    y={points.reduce((a, p) => a + p.y, 0) / points.length}
                    stroke="#b3b3b3"
                    strokeDasharray="4 4"
                  />
                </>
              )}
              <Scatter
                data={points}
                fill="var(--c-primary, #013344)"
                opacity={0.85}
                shape={(props: any) => {
                  const { cx, cy, payload } = props;
                  return (
                    <g>
                      <circle cx={cx} cy={cy} r={7} fill="var(--c-primary, #013344)" opacity={0.8} />
                      <text
                        x={cx + 10}
                        y={cy + 4}
                        fontSize={10}
                        fill="#4d4d4d"
                        fontFamily="'Satoshi:Medium',sans-serif"
                      >
                        {payload.athleteName}
                      </text>
                    </g>
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stato iniziale: nessun test selezionato */}
      {(!testIdX || !testIdY) && !sameTest && (
        <p className={styles.emptyNote}>Seleziona un test per ciascun asse per visualizzare la relazione.</p>
      )}
    </div>
  );
}
